/* eslint-disable @typescript-eslint/no-explicit-any */
// =====================================================
// COURSE BUILDER MODULE: Types, Validation, Persistence
// =====================================================

import { toast } from "sonner";
import {
  COURSE_BUILDER_META_KEY,
  COURSE_BUILDER_DRAFT_KEY,
  COURSE_BUILDER_STATE_KEY,
} from "../localStoragekeys";

// ------------------------------------
// COURSE BUILDER STATE MANAGEMENT
// ------------------------------------

export interface CourseBuilderState {
  mode: "ADD" | "VIEW" | "EDIT";
  currentStep: number;
  lastUpdated: number;
}

export const courseBuilderStateUtils = {
  // Save mode and current step to localStorage
  saveState: (mode: CourseBuilderState["mode"], currentStep: number): void => {
    try {
      const state: CourseBuilderState = {
        mode,
        currentStep,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(COURSE_BUILDER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn("Failed to save course builder state:", error);
    }
  },

  // Get mode and current step from localStorage
  getState: (): CourseBuilderState => {
    try {
      const savedState = localStorage.getItem(COURSE_BUILDER_STATE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Validate the parsed state
        if (parsed.mode && ["ADD", "VIEW", "EDIT"].includes(parsed.mode) && 
            typeof parsed.currentStep === "number" && parsed.currentStep >= 0 && parsed.currentStep <= 2) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn("Failed to parse course builder state:", error);
    }
    
    // Return default state
    return {
      mode: "ADD",
      currentStep: 0,
      lastUpdated: Date.now(),
    };
  },

  // Clear course builder state
  clearState: (): void => {
    try {
      localStorage.removeItem(COURSE_BUILDER_STATE_KEY);
    } catch (error) {
      console.warn("Failed to clear course builder state:", error);
    }
  },

  // Check if state exists
  hasState: (): boolean => {
    try {
      return localStorage.getItem(COURSE_BUILDER_STATE_KEY) !== null;
    } catch (error) {
      return false;
    }
  },
};

// ------------------------------------
// TYPE DEFINITIONS
// ------------------------------------

export type QuestionDifficulty =
  | "BEGINNER"
  | "INTERMEDIATE"
  | "ADVANCED"
  | "EXPERT";

export interface CoverImage {
  file: File | Record<string, unknown>;
  fileType: string;
  fileCategory: string;
  url: string;
}

export interface CourseInfoSection {
  id?: string;
  title: string;
  description: string;
  coverImage: CoverImage;
  tags: string[];
}

export interface MaterialContent {
  file: File | Record<string, unknown>;
  filename: string;
  fileType: string;
  fileCategory: string;
  url: string;
  thumbnailUrl?: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: { id: string; text: string };
  explanation: string;
  quizSetopicIdx: number;
  quizSetTitle: string;
}

export interface QuizSet {
  id: string;
  title: string;
  questions: QuizQuestion[];
  maxAttempts?: number;
  passingScore: number;
  weight: number;
}

export interface MaterialQuiz {
  title: string;
  quizSets: QuizSet[];
}

export interface Material {
  id: string;
  title: string;
  content: MaterialContent;
  quiz?: MaterialQuiz;
  duration?: number; // Optional duration in minutes
}

export interface Topic {
  id: string;
  title: string;
  description: string;
  materials?: Material[];
  estimatedDuration?: number;
  minPercentageCovered?: number;
}

export interface TopicsSet {
  topics: Topic[];
}

export interface AssessmentQuestion {
  type: "multiple_choice";
  question: string;
  options: string[];
  correctAnswer: { id: string; text: string };
  explanation: string;
  tags: string[];
  difficulty: QuestionDifficulty;
  updatedAt: string;
  createdAt: string;
  id: string;
  assessmentSetId: string;
  quizSetopicIdx: number;
  quizSetTitle: string;
}

export interface AssessmentSet {
  id: string;
  title: string;
  passingScore: number;
  questions: AssessmentQuestion[];
  sourceQuestions: AssessmentQuestion[];
  questionCount: number;
  updatedAt: string;
  maxAttempts?: number;
}

export interface AssessmentSection {
  assessment: {
    sets: AssessmentSet[];
  };
}

export type CourseBuilderData = [
  CourseInfoSection,
  TopicsSet,
  AssessmentSection,
];

export type CourseBuilderDraftItem =
  | CourseInfoSection
  | TopicsSet
  | AssessmentSection;

export type CourseBuilderDraft = CourseBuilderDraftItem[];

// ------------------------------------
// VALIDATION
// ------------------------------------

type ValidationError = { path: string; message: string };

export function validateCourseBuilderData(data: unknown): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!Array.isArray(data) || data.length !== 3) {
    errors.push({
      path: "root",
      message: "CourseBuilderData must be an array of length 3.",
    });
    return errors;
  }

  const [courseInfo, overview, assessment] = data as CourseBuilderData;

  // CourseInfo validation
  if (typeof courseInfo.title !== "string" || courseInfo.title.trim() === "") {
    errors.push({
      path: "[0].title",
      message: "Title must be a non-empty string.",
    });
  }
  if (typeof courseInfo.description !== "string") {
    errors.push({
      path: "[0].description",
      message: "Description must be a string.",
    });
  }
  if (
    typeof courseInfo.coverImage !== "object" ||
    courseInfo.coverImage === null
  ) {
    errors.push({
      path: "[0].coverImage",
      message: "CoverImage must be an object.",
    });
  } else {
    const ci = courseInfo.coverImage;
    if (typeof ci.url !== "string")
      errors.push({
        path: "[0].coverImage.url",
        message: "URL must be a string.",
      });
    if (typeof ci.fileType !== "string")
      errors.push({
        path: "[0].coverImage.fileType",
        message: "FileType must be a string.",
      });
    if (typeof ci.fileCategory !== "string")
      errors.push({
        path: "[0].coverImage.fileCategory",
        message: "FileCategory must be a string.",
      });
  }
  if (!Array.isArray(courseInfo.tags)) {
    errors.push({
      path: "[0].tags",
      message: "Tags must be an array of strings.",
    });
  }

  // Overview validation
  if (!Array.isArray(overview.topics)) {
    errors.push({ path: "[1].topics", message: "Topics must be an array." });
  } else {
    if (overview.topics.length === 0) {
      errors.push({
        path: "[1].topics",
        message: "At least one topic is required.",
      });
    }
    overview.topics.forEach((topic, topicIdx) => {
      if (!Array.isArray(topic.materials) || topic.materials.length === 0) {
        errors.push({
          path: `[1].topics[${topicIdx}].materials`,
          message: "Each topic must have at least one material.",
        });
      } else {
        topic.materials.forEach((material, matIdx) => {
          if (typeof material.duration !== "number" || material.duration < 0) {
            errors.push({
              path: `[1].topics[${topicIdx}].materials[${matIdx}].duration`,
              message: "Material duration must be a non-negative number.",
            });
          }
        });
      }
      if (typeof topic.id !== "string")
        errors.push({
          path: `[1].topics[${topicIdx}].id`,
          message: "Topic ID must be a string.",
        });
      if (typeof topic.title !== "string")
        errors.push({
          path: `[1].topics[${topicIdx}].title`,
          message: "Topic title must be a string.",
        });
      if (!Array.isArray(topic.materials)) return;
      topic.materials.forEach((mat, mIndex) => {
        if (typeof mat.id !== "string")
          errors.push({
            path: `[1].topics[${topicIdx}].materials[${mIndex}].id`,
            message: "Material ID must be a string.",
          });
        if (typeof mat.content?.url !== "string")
          errors.push({
            path: `[1].topics[${topicIdx}].materials[${mIndex}].content.url`,
            message: "Material URL must be a string.",
          });

        mat.quiz?.quizSets?.forEach((set, sIndex) => {
          if (!Array.isArray(set.questions)) {
            errors.push({
              path: `[1].topics[${topicIdx}].materials[${mIndex}].quiz.quizSets[${sIndex}].questions`,
              message: "Questions must be an array.",
            });
          } else {
            set.questions.forEach((q, qIndex) => {
              if (typeof q.question !== "string")
                errors.push({
                  path: `[1].topics[${topicIdx}].materials[${mIndex}].quiz.quizSets[${sIndex}].questions[${qIndex}].question`,
                  message: "Question must be a string.",
                });
              if (!Array.isArray(q.options) || q.options.length !== 4)
                errors.push({
                  path: `[1].topics[${topicIdx}].materials[${mIndex}].quiz.quizSets[${sIndex}].questions[${qIndex}].options`,
                  message: "Options must be an array of 4 strings.",
                });
              if (
                typeof q.correctAnswer !== "object" ||
                !q.correctAnswer.id ||
                !q.correctAnswer.text
              )
                errors.push({
                  path: `[1].topics[${topicIdx}].materials[${mIndex}].quiz.quizSets[${sIndex}].questions[${qIndex}].correctAnswer`,
                  message:
                    "Correct answer must be an object with id and text properties.",
                });
            });
          }
        });
      });
    });
  }

  // Assessment validation
  if (!Array.isArray(assessment?.assessment?.sets)) {
    errors.push({
      path: "[2].assessment.sets",
      message: "Assessment sets must be an array.",
    });
  } else {
    if (assessment.assessment.sets.length === 0) {
      errors.push({
        path: "[2].assessment.sets",
        message: "At least one assessment set (test) is required.",
      });
    }
    assessment.assessment.sets.forEach((set, quizSetopicIdx) => {
      if (typeof set.title !== "string")
        errors.push({
          path: `[2].assessment.sets[${quizSetopicIdx}].title`,
          message: "Set title must be a string.",
        });
      if (!Array.isArray(set.questions)) {
        errors.push({
          path: `[2].assessment.sets[${quizSetopicIdx}].questions`,
          message: "Questions must be an array.",
        });
      } else {
        if (set.questions.length === 0) {
          errors.push({
            path: `[2].assessment.sets[${quizSetopicIdx}].questions`,
            message:
              "Each assessment set (test) must have at least one question.",
          });
        }
        if (typeof set.questionCount === "number" && set.questionCount === 0) {
          errors.push({
            path: `[2].assessment.sets[${quizSetopicIdx}].questionCount`,
            message: "questionsPerTest (questionCount) can never be 0.",
          });
        }
        set.questions.forEach((q, qIndex) => {
          if (q.type !== "multiple_choice")
            errors.push({
              path: `[2].assessment.sets[${quizSetopicIdx}].questions[${qIndex}].type`,
              message: 'Only "multiple_choice" is allowed.',
            });
          if (!Array.isArray(q.options) || q.options.length !== 4)
            errors.push({
              path: `[2].assessment.sets[${quizSetopicIdx}].questions[${qIndex}].options`,
              message: "Options must be an array of 4 strings.",
            });
          if (!["easy", "medium", "hard"].includes(q.difficulty))
            errors.push({
              path: `[2].assessment.sets[${quizSetopicIdx}].questions[${qIndex}].difficulty`,
              message: 'Difficulty must be "easy", "medium", or "hard".',
            });
        });
      }
    });
  }

  return errors;
}

// ------------------------------------
// LOCAL STORAGE HELPERS
// ------------------------------------

import { fileStorageService } from "./fileStorageService";

export const loadDraft = async (): Promise<CourseBuilderDraft | null> => {
  try {
    const draft = localStorage.getItem(COURSE_BUILDER_DRAFT_KEY);
    if (!draft) return null;

    const parsedDraft = JSON.parse(draft);

    // Restore files from IndexedDB if they exist
    if (parsedDraft && Array.isArray(parsedDraft)) {
      // Restore files for topics (index 1)
      if (parsedDraft[1] && parsedDraft[1].topics) {
        for (const topic of parsedDraft[1].topics) {
          if (topic.materials) {
            for (const material of topic.materials) {
              if (material.content && material.content.fileId) {
                try {
                  const storedFile = await fileStorageService.getFile(
                    material.content.fileId
                  );
                  if (storedFile) {
                    material.content.file = storedFile.file;
                    material.content.url = URL.createObjectURL(storedFile.file);
                  }
                } catch (error) {
                  console.error(
                    `Error restoring file for material ${material.id}:`,
                    error
                  );
                  // Remove the fileId if file restoration failed
                  delete material.content.fileId;
                }
              }
            }
          }
        }
      }

      // Restore cover image for course info (index 0)
      if (
        parsedDraft[0] &&
        parsedDraft[0].coverImage &&
        parsedDraft[0].coverImage.fileId
      ) {
        try {
          const storedFile = await fileStorageService.getFile(
            parsedDraft[0].coverImage.fileId
          );
          if (storedFile) {
            parsedDraft[0].coverImage.file = storedFile.file;
            parsedDraft[0].coverImage.url = URL.createObjectURL(
              storedFile.file
            );
          }
        } catch (error) {
          console.error("Error restoring cover image:", error);
          delete parsedDraft[0].coverImage.fileId;
        }
      }
    }

    return parsedDraft;
  } catch (err) {
    console.error("Error loading draft:", err);
    return null;
  }
};

export const saveDraft = async (
  data: CourseBuilderDraft,
  showToast?: boolean
): Promise<boolean> => {
  try {
    // Store files in IndexedDB and replace File objects with fileIds
    const processedData = await processDraftForStorage(data);

    localStorage.setItem(
      COURSE_BUILDER_DRAFT_KEY,
      JSON.stringify(processedData)
    );
    if (showToast) toast.success("Draft saved");
    return true;
  } catch (err) {
    console.error("Error saving draft:", err);
    if (showToast) toast.error("Failed to save draft");
    return false;
  }
};

// Helper function to process draft data for storage
const processDraftForStorage = async (
  data: CourseBuilderDraft
): Promise<CourseBuilderDraft> => {
  const processedData = JSON.parse(JSON.stringify(data)); // Deep clone

  // Process course info (index 0)
  if (
    processedData[0] &&
    processedData[0].coverImage &&
    processedData[0].coverImage.file instanceof File
  ) {
    try {
      const fileId = await fileStorageService.storeFile(
        processedData[0].coverImage.file,
        {
          filename: processedData[0].coverImage.file.name,
          fileType: processedData[0].coverImage.fileType,
          fileSize: processedData[0].coverImage.file.size,
          fileCategory: processedData[0].coverImage.fileCategory,
        }
      );
      processedData[0].coverImage.fileId = fileId;
      delete processedData[0].coverImage.file;
      delete processedData[0].coverImage.url;
    } catch (error) {
      console.error("Error storing cover image:", error);
    }
  }

  // Process topics (index 1)
  if (processedData[1] && processedData[1].topics) {
    for (const topic of processedData[1].topics) {
      if (topic.materials) {
        for (const material of topic.materials) {
          if (material.content && material.content.file instanceof File) {
            try {
              const fileId = await fileStorageService.storeFile(
                material.content.file,
                {
                  filename:
                    material.content.filename || material.content.file.name,
                  fileType: material.content.fileType,
                  fileSize:
                    material.content.fileSize || material.content.file.size,
                  fileCategory: material.content.fileCategory,
                  topicId: topic.id,
                  materialId: material.id,
                }
              );
              material.content.fileId = fileId;
              delete material.content.file;
              delete material.content.url;
            } catch (error) {
              console.error(
                `Error storing file for material ${material.id}:`,
                error
              );
            }
          }
        }
      }
    }
  }

  return processedData;
};

export const clearDraft = async ({
  showToast = false,
} = {}): Promise<boolean> => {
  try {
    localStorage.removeItem(COURSE_BUILDER_DRAFT_KEY);
    localStorage.removeItem(COURSE_BUILDER_META_KEY);

    // Clear all stored files
    await fileStorageService.clearAllFiles();

    if (showToast) toast.success("Draft cleared");
    return true;
  } catch (err) {
    console.error("Error clearing draft:", err);
    if (showToast) toast.error("Failed to clear draft");
    return false;
  }
};

export function setCourseBuilderMeta(currentStep: number, mode: string | null) {
  let courseBuilderMeta: any = localStorage.getItem(COURSE_BUILDER_META_KEY);
  if (!courseBuilderMeta) {
    localStorage.setItem(
      COURSE_BUILDER_META_KEY,
      JSON.stringify({
        currentStep,
        mode,
        hasStepChanges: false,
        stepProcessedFlags: { 0: false, 1: false, 2: false },
      })
    );
    return;
  }

  courseBuilderMeta = JSON.parse(courseBuilderMeta);

  if (mode) courseBuilderMeta.mode = mode;
  courseBuilderMeta.currentStep = currentStep;
  localStorage.setItem(
    COURSE_BUILDER_META_KEY,
    JSON.stringify(courseBuilderMeta)
  );
}

// ------------------------------------
// QUIZ SET -> ASSESSMENT TRANSFORMER
// ------------------------------------

export const convertQuizSetsToAssessments = ():
  | AssessmentSection
  | undefined => {
  const courseBuilderDraftString = localStorage.getItem(
    COURSE_BUILDER_DRAFT_KEY
  );
  if (!courseBuilderDraftString) {
    return;
  }

  let courseBuilderDraft: CourseBuilderDraft;
  try {
    courseBuilderDraft = JSON.parse(courseBuilderDraftString) || [];
  } catch (error) {
    console.error("Failed to parse draft data", error);
    return;
  }

  if (
    !Array.isArray(courseBuilderDraft) ||
    !courseBuilderDraft.length ||
    !courseBuilderDraft[1]
  ) {
    return;
  }

  // Find the topics object (index 1 based on your structure)
  const topicsObject = courseBuilderDraft.find(
    (item): item is TopicsSet => "topics" in item
  );

  if (!topicsObject || !topicsObject.topics) {
    return {
      assessment: {
        sets: [],
      },
    };
  }

  // Collect all quiz sets and their questions
  const quizSetMap = new Map<string, QuizSet>();

  // Iterate through all topics and materials
  topicsObject.topics.forEach((topic: Topic) => {
    if (topic.materials) {
      topic.materials.forEach((material: Material) => {
        if (material.quiz && material.quiz.quizSets) {
          material.quiz.quizSets.forEach((quizSet: QuizSet) => {
            const quizSetTitle = quizSet.title ?? "";

            if (!quizSetMap.has(quizSetTitle)) {
              quizSetMap.set(quizSetTitle, {
                id: quizSet.id,
                title: quizSet.title,
                questions: [],
                passingScore: quizSet.passingScore || 70,
                weight: quizSet.weight || 1,
              });
            }

            // Add all questions from this quiz set
            if (quizSet.questions) {
              quizSet.questions.forEach((question: QuizQuestion) => {
                // Transform question format to match assessment structure
                // Correct answer is now always an object with id and text
                const correctAnswer = question.correctAnswer;

                const assessmentQuestion: AssessmentQuestion = {
                  type: "multiple_choice",
                  question: question.question,
                  options: question.options,
                  correctAnswer: correctAnswer,
                  explanation: question.explanation || "",
                  tags: [],
                  difficulty: "INTERMEDIATE" as const,
                  updatedAt: new Date().toISOString(),
                  id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  createdAt: new Date().toISOString(),
                  assessmentSetId: "", // Will be set below
                  quizSetopicIdx: question.quizSetopicIdx || 0,
                  quizSetTitle: question.quizSetTitle || quizSetTitle,
                };

                const setData = quizSetMap.get(quizSetTitle);
                if (setData) {
                  setData.questions.push(assessmentQuestion);
                }
              });
            }
          });
        }
      });
    }
  });

  // Create assessment sets from the collected quiz sets
  const assessmentSets: AssessmentSet[] = [];
  let quizSetopicIdx = 1;

  quizSetMap.forEach((setData: QuizSet) => {
    const assessmentSetId = `set-${Date.now()}-${quizSetopicIdx}`;
    const assessmentTitle = `Assessment ${quizSetopicIdx}`;

    // Convert quiz questions to assessment questions for source questions
    const sourceQuestions: AssessmentQuestion[] = setData.questions.map(
      (question: QuizQuestion, index: number) => {
        // Correct answer is now always an object with id and text
        const correctAnswer = question.correctAnswer;

        return {
          type: "multiple_choice" as const,
          question: question.question,
          options: question.options,
          correctAnswer: correctAnswer,
          explanation: question.explanation || "",
          tags: [],
          difficulty: "INTERMEDIATE" as const,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          assessmentSetId: assessmentSetId,
          quizSetopicIdx: index,
          quizSetTitle: setData.title || `Quiz Set ${quizSetopicIdx}`,
        };
      }
    );

    const assessmentSet: AssessmentSet = {
      id: assessmentSetId,
      title: assessmentTitle,
      passingScore: setData.passingScore,
      questions: sourceQuestions,
      sourceQuestions: sourceQuestions,
      maxAttempts: 3,
      questionCount: setData.questions.length,
      updatedAt: new Date().toISOString(),
    };

    assessmentSets.push(assessmentSet);
    quizSetopicIdx++;
  });

  // Find or create the assessment object in the courseBuilderDraft
  let assessmentObject = courseBuilderDraft.find(
    (item): item is AssessmentSection => "assessment" in item
  );

  if (!assessmentObject) {
    // Create new assessment object if it doesn't exist
    assessmentObject = {
      assessment: {
        sets: [],
      },
    };
    courseBuilderDraft.push(assessmentObject);
  }

  // Replace the existing assessment sets with the new ones
  assessmentObject.assessment.sets = assessmentSets;
  localStorage.setItem(
    COURSE_BUILDER_DRAFT_KEY,
    JSON.stringify(courseBuilderDraft)
  );

  return assessmentObject;
};

export type course_builder_form_field_to_api_field_mapping = [
  // Click on "Next" at Step 1 : Call CreateCourse API
  // Click on "Next" at Step 2 : Call CreateTopic API -> Call CreateLearningMaterial API -> Call CreateTest API -> Call CreateQuestionSet API -> CreateQuestion API
  // Click on "Next" at Step 3 : Call CreateAssessment API

  // COURSE INFORMATION
  {
    title: string; // -> CreateCourse : name
    description: string; // -> CreateCourse : description
    coverImage: {
      fileType: "IMAGE"; // -> UploadRequest -> fileType
      fileCategory: "GENERAL"; // -> UploadRequest : in backend, not frontend
      // -> TODO : CreateCourse : not in backend
      file: File;
      url: string;
    };
    tags: string[]; // -> CreateCourse : tags
    // isActive: boolean; // -> CreateCourse : isActive, default is false
  },
  // TOPIC LIST, MATERIAL LIST AND MATERIAL QUIZ LIST
  {
    topics: {
      id: string; // -> CreateLearningMaterial : topicId
      title: string; // -> CreateTopic : name
      description: string; // -> CreateTopic : description
      // estimatedDuration: number; // -> CreateTopic : estimatedDuration, [OPTIONAL]
      // minPercentageCovered: number; // -> CreateTopic : minPercentageCovered, [OPTIONAL]
      // isActive: boolean; // -> CreateTopic : isActive, default is true
      materials: {
        id: string; // -> CreateTest : learningMaterialId and UploadRequest : materialId
        title: string; // -> CreateLearningMaterial : title
        // TODO => Add category: 'LEARNING_MATERIAL'; // -> UploadRequest : in backend, not frontend
        content: {
          url: string;
          file: File;
          fileType: "PDF" | "PPTX" | "VIDEO" | "IMAGE" | "DOCUMENT"; // -> UploadRequest : fileType
          fileCategory: "LEARNING_MATERIAL"; // -> UploadRequest : fileCategory
        };
        // description: string -> CreateLearningMaterial : description [OPTIONAL]
        // tags: string[] -> CreateLearningMaterial : tags [OPTIONAL]
        // category: string -> CreateLearningMaterial : category [OPTIONAL]
        // subCategory: string -> CreateLearningMaterial : subCategory [OPTIONAL]
        // difficulty: string -> CreateLearningMaterial : difficulty [OPTIONAL]
        // allowDownload: boolean -> CreateLearningMaterial : allowDownload [OPTIONAL]
        // drmEnabled: boolean -> CreateLearningMaterial : drmEnabled [OPTIONAL]
        // isActive: boolean -> CreateLearningMaterial : isActive, default is true
        quiz: {
          title: string; // -> CreateTest : title
          // description: string; // -> CreateTest : description [OPTIONAL]
          // type: 'QUIZ' | 'ASSESSMENT'; // -> CreateTest : type [ENUM]
          // difficulty: string; // -> CreateTest : difficulty [OPTIONAL]
          // timeLimit: number -> CreateTest : timeLimit [OPTIONAL]
          // maxAttempts: number -> CreateTest : maxAttempts [OPTIONAL]
          // passingScore: number -> CreateTest : passingScore
          // showCorrectAnswers: boolean -> CreateTest : showCorrectAnswers
          // allowRetakes: boolean -> CreateTest : allowRetakes
          // enableRandomization: boolean; // -> CreateTest : enableRandomization
          // questionsPerTest: number; // -> CreateTest : questionsPerTest
          // useQuestionSetId?: string; // -> CreateTest : useQuestionSetId [OPTIONAL]
          // gradingType: 'MANUAL' | 'AUTOMATIC' | 'HYBRID'; // -> CreateTest : gradingType
          quizSets: {
            passingScore: number; // -> CreateTest : passingScore
            id: string; // -> CreateQuestionSet : testId
            title: string; // -> CreateQuestionSet : name
            weight: number; // -> CreateQuestionSet : weight [OPTIONAL]
            // description: string; // -> CreateQuestionSet : description [OPTIONAL]
            // tags: string[]; // -> CreateQuestionSet : tags [OPTIONAL]
            questions: {
              id: string; // -> [USELESS]
              question: string; // -> CreateQuestion : body.question
              options: string[]; // -> CreateQuestion : body.options
              correctAnswer: { id: string; text: string }; // -> CreateQuestion : body.correctAnswer
              explanation: string; // -> CreateQuestion : body.explanation [OPTIONAL]
              quizSetopicIdx: number; // -> [USELESS]
              quizSetTitle: string; // -> [USELESS]
            }[];
          }[];
        };
      }[];
    }[];
  },
  // ASSESSMENT SETS
  {
    assessment: {
      sets: {
        id: string; // -> CreateQuestionSet : testId
        title: string; // -> CreateTest : title
        passingScore: number; // -> CreateTest : passingScore
        questions: {
          type: "multiple_choice"; // -> [USELESS]
          question: string; // -> CreateQuestion : body.question
          options: string[]; // -> CreateQuestion : body.options
          correctAnswer: { id: string; text: string }; // -> CreateQuestion : body.correctAnswer
          explanation: string; // -> CreateQuestion : body.explanation [OPTIONAL]
          weight: number; // -> [USELESS]
          tags: string[]; // -> CreateQuestion : body.tags
          difficulty: "easy" | "medium" | "hard";
          updatedAt: string; // -> [USELESS]
          createdAt: string; // -> [USELESS]
          id: string; // -> [USELESS]
          assessmentSetId: string; // -> [USELESS]
        }[];
        questionCount?: number;
        updatedAt?: string;
      }[];
      passingScore: number; // -> [USELESS]
    };
  },
];

export const sampleData = [
  {
    title: "lorem ipsum, lorem ipsum",
    description: "lorem ipsum, lorem ipsum",
    coverImage: {
      file: {},
      fileType: "IMAGE",
      fileCategory: "GENERAL",
      url: "data:image/png;base64,...",
    },
    tags: ["lorem ipsum, lorem ipsum", "lorem ipsum"],
  },
  {
    topics: [
      {
        id: "topic-1753539434039",
        title: "Topic 1",
        description: "lorem ipsum, lorem ipsum",
        materials: [
          {
            id: "material-1753539436535",
            title: "New Material 1",
            content: {
              fileType: "PDF",
              url: "",
              file: null,
            },
            quiz: {
              title: "Quiz for New Material 1",
              quizSets: [
                {
                  id: "set-1753539439710",
                  title: "Quiz Set 1",
                  questions: [
                    {
                      id: "q-1753539441276-380",
                      question: "lorem ipsum, lorem ipsum",
                      options: [
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                      ],
                      correctAnswer: {
                        id: "0",
                        text: "lorem ipsum, lorem ipsum",
                      },
                      explanation: "",
                      quizSetopicIdx: 0,
                      quizSetTitle: "Quiz Set 1",
                    },
                    {
                      id: "q-1753539445742-214",
                      question: "lorem ipsum, lorem ipsum",
                      options: [
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                      ],
                      correctAnswer: {
                        id: "1",
                        text: "lorem ipsum, lorem ipsum",
                      },
                      explanation: "",
                      quizSetopicIdx: 0,
                      quizSetTitle: "Quiz Set 1",
                    },
                  ],
                  passingScore: 75,
                  weight: 1,
                },
                {
                  id: "set-1753539452677",
                  title: "Quiz Set 2",
                  questions: [
                    {
                      id: "q-1753539453939-47",
                      question: "lorem ipsum, lorem ipsum",
                      options: [
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                      ],
                      correctAnswer: {
                        id: "2",
                        text: "lorem ipsum, lorem ipsum",
                      },
                      explanation: "",
                      quizSetopicIdx: 1,
                      quizSetTitle: "Quiz Set 2",
                    },
                    {
                      id: "q-1753539461064-234",
                      question: "lorem ipsum, lorem ipsum",
                      options: [
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                        "lorem ipsum, lorem ipsum",
                      ],
                      correctAnswer: {
                        id: "2",
                        text: "lorem ipsum, lorem ipsum",
                      },
                      explanation: "",
                      quizSetopicIdx: 1,
                      quizSetTitle: "Quiz Set 2",
                    },
                  ],
                  passingScore: 75,
                  weight: 1,
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    assessment: {
      sets: [
        {
          id: "set-1753539575669-1",
          title: "Assessment 1",
          passingScore: 75,
          questions: [
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "1", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-z4s1tirnb",
              assessmentSetId: "set-1753539575669-1",
              quizSetopicIdx: 1,
              quizSetTitle: "Quiz Set 1",
            },
          ],
          sourceQuestions: [
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "0", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-ub52dsz1q",
              assessmentSetId: "set-1753539575669-1",
              quizSetopicIdx: 0,
              quizSetTitle: "Quiz Set 1",
            },
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: 1,
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-z4s1tirnb",
              assessmentSetId: "set-1753539575669-1",
              quizSetopicIdx: 1,
              quizSetTitle: "Quiz Set 1",
            },
          ],
          questionCount: 1,
          updatedAt: "2025-07-26T14:20:09.538Z",
          maxAttempts: 3,
        },
        {
          id: "set-1753539575669-2",
          title: "Assessment 2",
          passingScore: 75,
          questions: [
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "2", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-s58c8zfs4",
              assessmentSetId: "set-1753539575669-2",
              quizSetopicIdx: 0,
              quizSetTitle: "Quiz Set 2",
            },
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "3", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-t6q0vbx3i",
              assessmentSetId: "set-1753539575669-2",
              quizSetopicIdx: 1,
              quizSetTitle: "Quiz Set 2",
            },
          ],
          sourceQuestions: [
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "2", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-s58c8zfs4",
              assessmentSetId: "set-1753539575669-2",
              quizSetopicIdx: 0,
              quizSetTitle: "Quiz Set 2",
            },
            {
              type: "multiple_choice",
              question: "lorem ipsum, lorem ipsum",
              options: [
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
                "lorem ipsum, lorem ipsum",
              ],
              correctAnswer: { id: "3", text: "lorem ipsum, lorem ipsum" },
              explanation: "",
              tags: [],
              difficulty: "medium",
              updatedAt: "2025-07-26T14:19:35.669Z",
              createdAt: "2025-07-26T14:19:35.669Z",
              id: "q-1753539575669-t6q0vbx3i",
              assessmentSetId: "set-1753539575669-2",
              quizSetopicIdx: 1,
              quizSetTitle: "Quiz Set 2",
            },
          ],
          questionCount: 2,
          updatedAt: "2025-07-26T14:19:35.669Z",
          maxAttempts: 3,
        },
      ],
    },
  },
];
